import React from "react";
import styled from "styled-components";
import { Link } from "@inertiajs/react";
import { UtensilsCrossed, BedDouble } from "lucide-react";
import { PageHeader } from "../../../../components/page-header";
import { useLanguage } from "../../../../contexts/language";
import { useNotification } from "../../../../contexts/notification";
import {
  saveOrderToLocalHistory,
  getOrderKindFromLocal,
  orderApiPath,
} from "../../../../util";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

// The API sends money pre-formatted ("Rp5.000"), so "is there a discount" is a digits check rather
// than a numeric comparison — the separators differ by locale and currency.
const isZeroMoney = (formatted) =>
  !String(formatted).replace(/\D/g, "").replace(/0/g, "");

/* Notifications are written server-side in English and stored, so the row already in the database
 * cannot be re-languaged at the source without a backfill. Keying on notification_type instead means
 * old rows and new rows both display correctly, and an unrecognised type still shows something real
 * rather than a blank card — which is what makes it safe to add types later.
 */
const notificationCopy = (notification, order, t) => {
  const template =
    t.orderDetail.notificationCopy?.[notification.notification_type];
  if (!template) {
    return { title: notification.title, message: notification.message };
  }
  return {
    title: template.title,
    message: template.message.replace("%{number}", order.order_number || ""),
  };
};

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #f7f7f7 45%, #f4f4f4 100%);
  padding: 20px 16px 28px;
`;

const Wrapper = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 20px 20px 18px;
  margin-bottom: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.03);
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const StatusLabel = styled.div`
  font-size: 13px;
  color: #666;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(props) => props.$color || "#5f5f5f"};
  background: ${(props) => props.$background || "#f1f1f1"};
`;

const TotalRow = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ececec;
  display: flex;
  align-items: end;
  justify-content: space-between;
`;

const TotalLabel = styled.div`
  font-size: 13px;
  color: #666;
`;

const TotalValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #c9a961;
`;

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const TimeValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333;
`;

const ActionRow = styled.div`
  margin-top: 14px;
`;

const Button = styled.a`
  display: inline-block;
  padding: 11px 16px;
  border-radius: 10px;
  background: #c9a961;
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #b69352;
    box-shadow: 0 6px 16px rgba(201, 169, 97, 0.35);
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 14px;
  font-size: 28px;
  font-weight: 600;
  color: #1f1f1f;
`;

const InvoiceBox = styled.div`
  margin: 0 0 14px;
  padding: 10px 12px;
  border: 1px solid #ecdfba;
  border-radius: 10px;
  background: #fbf6e9;
`;

const InvoiceLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #927a3a;
  margin-bottom: 2px;
`;

const InvoiceValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #5f4e1d;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  word-break: break-all;
`;

/* At the TOP of the page on purpose. The only thing that used to mention pick-up was the
 * notification card at the bottom, which appears after the kitchen finishes — by which point the
 * guest has already been waiting for a delivery that was never coming.
 */
const PickupBanner = styled.div`
  align-items: flex-start;
  background: #fff7e8;
  border: 1px solid #f2dfba;
  border-radius: 12px;
  display: flex;
  gap: 0.65rem;
  margin-bottom: 1rem;
  padding: 0.85rem 0.95rem;
`;

const PickupIcon = styled.div`
  color: #a15c00;
  flex-shrink: 0;
  margin-top: 2px;
`;

const PickupTitle = styled.div`
  color: #6b3e00;
  font-size: 0.95rem;
  font-weight: 800;
  margin-bottom: 0.15rem;
`;

const PickupText = styled.div`
  color: #6b3e00;
  font-size: 0.87rem;
  line-height: 1.5;
`;

const BodySectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: #222;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

// An invoice, not a packing list: every item shows what one costs, how many were ordered and what
// that line came to, so the total can be checked by anyone holding a phone.
const InvoiceRow = styled.li`
  border-bottom: 1px solid #f0f0f0;
  padding: 10px 0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }
`;

const InvoiceLineTop = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
`;

const InvoiceName = styled.div`
  color: #2b2b2b;
  font-size: 15px;
  font-weight: 500;
  min-width: 0;
`;

const InvoiceLineTotal = styled.div`
  color: #2b2b2b;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
`;

const InvoiceMath = styled.div`
  color: #6b6b6b;
  font-size: 13px;
  margin-top: 2px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  font-size: 14px;
  color: #555;
`;

const SummaryValue = styled.div`
  font-weight: 600;
  color: ${(props) => (props.$discount ? "#0f7a39" : "#333")};
  white-space: nowrap;
`;

const GrandTotalRow = styled(SummaryRow)`
  align-items: baseline;
  border-top: 1px solid #ececec;
  margin-top: 12px;
  padding-top: 12px;
`;

const GrandTotalLabel = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1f1f1f;
`;

const GrandTotalValue = styled.div`
  color: #c9a961;
  font-size: 22px;
  font-weight: 700;
  white-space: nowrap;
`;

const ListItem = styled.li`
  border: 1px solid #efefef;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fcfcfc;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const ItemHeadline = styled.div`
  font-size: 15px;
  color: #2b2b2b;
  font-weight: 500;
`;

const ItemNote = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #767676;
`;

const EmptyState = styled.div`
  color: #888;
  font-size: 14px;
`;

const OrderMoreRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
`;

const OrderMoreLink = styled(Link)`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 10px;
  border: 1px dashed #c9a961;
  border-radius: 12px;
  background: rgba(201, 169, 97, 0.04);
  color: #c9a961;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(201, 169, 97, 0.1);
    box-shadow: 0 2px 8px rgba(201, 169, 97, 0.15);
  }
`;

export default function OrderShowPage(props) {
  const { building_code, order_id } = props;
  const { t } = useLanguage();
  const { subscribe, notificationState } = useNotification();
  const [order, setOrder] = React.useState(null);
  const [hasAutoOpenedPayment, setHasAutoOpenedPayment] = React.useState(false);

  const loadOrder = React.useCallback(async () => {
    const kind = getOrderKindFromLocal(building_code, order_id);
    const response = await fetch(orderApiPath({ id: order_id, kind }), {
      headers: { Accept: "application/json" },
    });
    const payload = await response.json();
    setOrder(payload?.data || null);
  }, [order_id, building_code]);

  React.useEffect(() => {
    loadOrder();
    const interval = setInterval(loadOrder, 10000);
    return () => clearInterval(interval);
  }, [loadOrder]);

  React.useEffect(() => {
    if (!order?.id) return;
    saveOrderToLocalHistory({ buildingCode: building_code, order });
  }, [order, building_code]);

  React.useEffect(() => {
    if (hasAutoOpenedPayment || !order?.payment_link_url) return;

    const queryParams = new URLSearchParams(window.location.search);
    const shouldAutoPay = queryParams.get("auto_pay") === "1";
    if (!shouldAutoPay) return;

    const continueToPayment = async () => {
      setHasAutoOpenedPayment(true);
      await subscribe();
      window.location.assign(order.payment_link_url);
    };

    continueToPayment();
  }, [order, hasAutoOpenedPayment, subscribe]);

  const renderStatusBadge = (type, value) => {
    const normalized = String(value || "-").toLowerCase();

    if (type === "payment") {
      if (normalized === "paid") {
        return (
          <StatusBadge $color="#0f7a39" $background="#dff7e8">
            {t.orderDetail.paid}
          </StatusBadge>
        );
      }
      if (normalized === "pending") {
        return (
          <StatusBadge $color="#9a6a00" $background="#fff4da">
            {t.orderDetail.pending}
          </StatusBadge>
        );
      }
      if (
        normalized === "expired" ||
        normalized === "failed" ||
        normalized === "canceled"
      ) {
        return (
          <StatusBadge $color="#a1152d" $background="#ffe3e8">
            {value}
          </StatusBadge>
        );
      }
      return <StatusBadge>{value || "-"}</StatusBadge>;
    }

    if (type === "delivery") {
      if (normalized === "on_progress") {
        return (
          <StatusBadge $color="#155d9b" $background="#e4f2ff">
            {t.orderDetail.onProgress}
          </StatusBadge>
        );
      }
      if (normalized === "finished") {
        return (
          <StatusBadge $color="#0f7a39" $background="#dff7e8">
            {t.orderDetail.finished}
          </StatusBadge>
        );
      }
      if (normalized === "pending_payment") {
        return (
          <StatusBadge $color="#9a6a00" $background="#fff4da">
            {t.orderDetail.pendingPayment}
          </StatusBadge>
        );
      }
      return <StatusBadge>{value || "-"}</StatusBadge>;
    }

    return <StatusBadge>{value || "-"}</StatusBadge>;
  };

  return (
    <Container>
      <PageHeader
        title={order?.order_number || ""}
        backPath={`/hebe/${building_code}/hgs/orders`}
        backLabel={t.header.backToOrders}
        showGuestInfo={false}
        buildingCode={building_code}
      />
      <Wrapper>
        {!order ? (
          <Card>{t.order.loadingOrder}</Card>
        ) : (
          <>
            <PickupBanner>
              <PickupIcon>
                <UtensilsCrossed size={20} />
              </PickupIcon>
              <div>
                <PickupTitle>{t.orderDetail.pickupTitle}</PickupTitle>
                <PickupText>
                  {t.order.pickupInfo}{" "}
                  {notificationState === "subscribed"
                    ? t.orderDetail.pickupWhereReady
                    : t.orderDetail.pickupWhereRefresh}
                </PickupText>
              </div>
            </PickupBanner>
            <Card>
              <SectionTitle>{order.order_number}</SectionTitle>
              {order.invoice_number ? (
                <InvoiceBox>
                  <InvoiceLabel>{t.orderDetail.invoiceNumber}</InvoiceLabel>
                  <InvoiceValue>{order.invoice_number}</InvoiceValue>
                </InvoiceBox>
              ) : null}
              <StatusRow>
                <StatusLabel>{t.orderDetail.paymentStatus}</StatusLabel>
                {renderStatusBadge("payment", order.payment_status)}
              </StatusRow>
              <StatusRow>
                <StatusLabel>{t.orderDetail.deliveryStatus}</StatusLabel>
                {renderStatusBadge("delivery", order.delivery_status)}
              </StatusRow>
              <TotalRow>
                <TotalLabel>{t.orderDetail.totalAmount}</TotalLabel>
                <TotalValue>{order.total}</TotalValue>
              </TotalRow>
              {order.created_at ? (
                <TimeRow>
                  <StatusLabel>{t.orderDetail.orderedAt}</StatusLabel>
                  <TimeValue>{formatDateTime(order.created_at)}</TimeValue>
                </TimeRow>
              ) : null}
              {order.paid_at ? (
                <TimeRow>
                  <StatusLabel>{t.orderDetail.paidAt}</StatusLabel>
                  <TimeValue>{formatDateTime(order.paid_at)}</TimeValue>
                </TimeRow>
              ) : null}
              {order.finished_at ? (
                <TimeRow>
                  <StatusLabel>{t.orderDetail.finishedAt}</StatusLabel>
                  <TimeValue>{formatDateTime(order.finished_at)}</TimeValue>
                </TimeRow>
              ) : null}
              {order.payment_link_url ? (
                <ActionRow>
                  <Button
                    href={order.payment_link_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t.orderDetail.openPaymentLink}
                  </Button>
                </ActionRow>
              ) : null}
            </Card>

            <Card>
              <BodySectionTitle>{t.orderDetail.orderItems}</BodySectionTitle>
              <List>
                {(order.lines || []).map((line) => (
                  <InvoiceRow key={line.id}>
                    <InvoiceLineTop>
                      <InvoiceName>{line.name}</InvoiceName>
                      {line.line_total ? (
                        <InvoiceLineTotal>{line.line_total}</InvoiceLineTotal>
                      ) : null}
                    </InvoiceLineTop>
                    {line.unit_price ? (
                      <InvoiceMath>
                        {line.unit_price} × {line.quantity}
                      </InvoiceMath>
                    ) : (
                      <InvoiceMath>× {line.quantity}</InvoiceMath>
                    )}
                    {line.note ? (
                      <ItemNote>
                        {t.orderDetail.notePrefix}: {line.note}
                      </ItemNote>
                    ) : null}
                  </InvoiceRow>
                ))}
              </List>

              {order.subtotal ? (
                <SummaryRow>
                  <div>{t.orderDetail.subtotal}</div>
                  <SummaryValue>{order.subtotal}</SummaryValue>
                </SummaryRow>
              ) : null}
              {order.discount && !isZeroMoney(order.discount) ? (
                <SummaryRow>
                  <div>{t.orderDetail.discount}</div>
                  <SummaryValue $discount>- {order.discount}</SummaryValue>
                </SummaryRow>
              ) : null}
              {order.tax ? (
                <SummaryRow>
                  <div>{order.tax_label}</div>
                  <SummaryValue>{order.tax}</SummaryValue>
                </SummaryRow>
              ) : null}
              <GrandTotalRow>
                <GrandTotalLabel>{t.orderDetail.totalAmount}</GrandTotalLabel>
                <GrandTotalValue>{order.total}</GrandTotalValue>
              </GrandTotalRow>
            </Card>

            <Card>
              <BodySectionTitle>{t.orderDetail.notifications}</BodySectionTitle>
              {(order.notifications || []).length === 0 ? (
                <EmptyState>{t.orderDetail.noNotificationsYet}</EmptyState>
              ) : (
                <List>
                  {order.notifications.map((notification) => {
                    const copy = notificationCopy(notification, order, t);
                    return (
                      <ListItem key={notification.id}>
                        <ItemHeadline>{copy.title}</ItemHeadline>
                        <ItemNote>{copy.message}</ItemNote>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Card>

            <OrderMoreRow>
              <OrderMoreLink href={`/hebe/${building_code}/hgs/fnb`}>
                <UtensilsCrossed size={16} />
                {t.orders.orderMoreFnb}
              </OrderMoreLink>
              <OrderMoreLink
                href={`/hebe/${building_code}/hgs/add-on-facility`}
              >
                <BedDouble size={16} />
                {t.orders.orderMoreAddOn}
              </OrderMoreLink>
            </OrderMoreRow>
          </>
        )}
      </Wrapper>
    </Container>
  );
}
